import { AddAffordance } from '@atlas/ui-components'
import { Tag } from '@atlas/ui-primitives'
import { useState } from 'react'
import type { PanelAxis } from './types'

export interface AddAxisButtonProps {
	/** Axes not currently shown. Renders nothing when empty. */
	hidden: PanelAxis[]
	onAdd: (axisCode: string) => void
	/** Names the surface being added to, for screen readers. */
	ariaContext: string
	size?: 'sm' | 'md'
}

/**
 * Reveals one more axis.
 *
 * Shared by the batch rail and the per-file card so the two never drift: both
 * render whatever the axis registry returns, and adding a dimension to the DB
 * grows this list with no code change. The count is shown because "+ Add
 * dimension" alone gives no sense of whether two or twelve are waiting.
 */
export function AddAxisButton({ hidden, onAdd, ariaContext, size = 'md' }: AddAxisButtonProps) {
	const [isOpen, setIsOpen] = useState(false)

	if (hidden.length === 0) return null

	return (
		<div className={size === 'sm' ? '' : 'border-t border-dashed border-ds-border pt-4'}>
			<AddAffordance
				block
				size={size}
				label="Add dimension"
				hint={`${hidden.length} more available`}
				aria-expanded={isOpen}
				aria-label={`Add dimension ${ariaContext}`}
				onPress={() => setIsOpen((open) => !open)}
			/>
			{isOpen && (
				<div className="mt-2 flex flex-wrap gap-1.5">
					{hidden.map((axis) => (
						<button
							key={axis.code}
							type="button"
							onClick={() => {
								onAdd(axis.code)
								setIsOpen(false)
							}}
						>
							<Tag variant="primary" size="sm">
								{axis.label}
							</Tag>
						</button>
					))}
				</div>
			)}
		</div>
	)
}
